import React, { useState, useEffect } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import Modal from '../shared/Modal';
import { FinanceProduct, InvoiceLineItem, InvoiceRecord, Application } from '../../types';
import { auth, db } from '../../lib/firebase';
import {
  X, Plus, Trash2, DollarSign, Calculator, AlertCircle, Sparkles, Check, FileText, ChevronDown
} from 'lucide-react';
import { getSafeApplicantName } from '../../lib/nameUtils';
import { workspaceHeader } from '../../lib/workspaces';
import SearchableSelect from '../shared/SearchableSelect';

const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};

// Keep native select popups from blowing out the modal width — native selects
// size their dropdown list to the longest option, so over-long labels are tidied.
const tidy = (s: any, max = 56): string => {
  const t = String(s ?? '').trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
};
const applicantOption = (a: any): string =>
  `${tidy(getSafeApplicantName(a), 34)} — ${tidy(a.programName || a.programType || a.assignment?.programName || 'Program', 26)} (${tidy(a.userEmail || 'no email', 34)})`;


interface InvoiceBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoiceData: Partial<InvoiceRecord>) => Promise<void>;
  existingInvoice?: InvoiceRecord | null;
  applications?: Application[];
  applicant?: Application;
  products: FinanceProduct[];
  preselectedApplicationId?: string;
  branding?: any;
}

export default function InvoiceBuilderModal({
  isOpen,
  onClose,
  onSave,
  existingInvoice,
  applications = [],
  applicant,
  products,
  preselectedApplicationId,
  branding,
}: InvoiceBuilderModalProps) {
  const effectiveApplications = applications.length > 0 ? applications : (applicant ? [applicant] : []);
  useBodyScrollLock(isOpen);

  const [creationMode, setCreationMode] = useState<'prompt' | 'builder'>('prompt');
  const [applicantId, setApplicantId] = useState(preselectedApplicationId || '');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14); // default 14-day due date
    return d.toISOString().slice(0, 10);
  });
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('Thank you for choosing our services. Please make payments by the due date.');
  const [internalNotes, setInternalNotes] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Sequence state & catalogs
  const [programsList, setProgramsList] = useState<any[]>([]);
  const [sponsorsList, setSponsorsList] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [selectedVariableId, setSelectedVariableId] = useState('');
  const [selectedSponsorId, setSelectedSponsorId] = useState('');

  // Selected application object
  const currentApp = effectiveApplications.find(a => (a.applicationId || (a as any).id) === applicantId);

  useEffect(() => {
    if (!currentApp) return;
    const pId = currentApp.programId || currentApp.programType || '';
    const vId = (currentApp as any).pricingVariableId || (currentApp as any).pricingVariableSnapshot?.pricingVariableId || '';
    const sId = currentApp.sponsorId || (currentApp as any).assignedSponsor || (currentApp as any).sponsorName || '';
    setSelectedProgramId(pId);
    setSelectedVariableId(vId);
    setSelectedSponsorId(sId);
  }, [currentApp]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchCatalogs = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const [progRes, sponsRes, tmplRes] = await Promise.allSettled([
          wsFetch('/api/admin/programs', { headers: { Authorization: `Bearer ${token}` } }),
          wsFetch('/api/admin/sponsors', { headers: { Authorization: `Bearer ${token}` } }),
          wsFetch('/api/admin/finance/invoice-templates', { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        if (progRes.status === 'fulfilled' && progRes.value.ok) {
          const data = await progRes.value.json();
          setProgramsList(data.programs || []);
        }
        if (sponsRes.status === 'fulfilled' && sponsRes.value.ok) {
          const data = await sponsRes.value.json();
          setSponsorsList(data.sponsors || []);
        }
        if (tmplRes.status === 'fulfilled' && tmplRes.value.ok) {
          const data = await tmplRes.value.json();
          setTemplates((data.templates || []).filter((t: any) => {
            if (Array.isArray(t.types)) return t.types.includes('invoice');
            return !t.type || t.type === 'invoice';
          }));
        }
      } catch (e) {
        console.error('Failed to fetch catalogs/templates:', e);
      }
    };
    fetchCatalogs();
  }, [isOpen]);

  // Derived program object and available variables/sponsors
  const activeProgramObj = programsList.find(p => p.id === selectedProgramId || p.programId === selectedProgramId);
  const availableVariables = Array.isArray(activeProgramObj?.pricingVariables)
    ? activeProgramObj.pricingVariables.filter((v: any) => v.visibility === 'visible')
    : [];

  const availableAcdSponsors = React.useMemo(() => {
    if (!activeProgramObj || !selectedVariableId) return sponsorsList;
    const acds = Array.isArray(activeProgramObj.acdRecords) ? activeProgramObj.acdRecords : [];
    const activeVarAcds = acds.filter((a: any) => a.pricingVariableId === selectedVariableId && a.active !== false);
    if (activeVarAcds.length > 0) {
      const allowedSponsorIds = activeVarAcds.map((a: any) => a.sponsorId);
      return sponsorsList.filter(s => allowedSponsorIds.includes(s.sponsorId || s.id));
    }
    return sponsorsList;
  }, [activeProgramObj, selectedVariableId, sponsorsList]);

  // Filter templates based on Program -> Variable -> Sponsor ACD relationship
  const matchingTemplates = React.useMemo(() => {
    if (!activeProgramObj || !selectedVariableId || !selectedSponsorId) return [];
    const acds = Array.isArray(activeProgramObj.acdRecords) ? activeProgramObj.acdRecords : [];
    const matchedAcd = acds.find(
      (a: any) =>
        a.pricingVariableId === selectedVariableId &&
        a.sponsorId === selectedSponsorId &&
        a.active !== false
    );
    if (!matchedAcd || !matchedAcd.invoiceTemplateId) return [];
    const linkedTmplId = matchedAcd.invoiceTemplateId;
    return templates.filter(t => t.id === linkedTmplId && t.active !== false);
  }, [templates, activeProgramObj, selectedVariableId, selectedSponsorId]);

  useEffect(() => {
    if (!isOpen) return;
    if (existingInvoice) {
      setCreationMode('builder');
    } else {
      setCreationMode('prompt');
    }
  }, [isOpen, existingInvoice]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchTemplates = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await wsFetch('/api/admin/finance/invoice-templates', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTemplates(data.templates || []);
        }
      } catch (e) {
        console.error('Failed to fetch invoice templates:', e);
      }
    };
    fetchTemplates();
  }, [isOpen]);

  const handleApplyTemplate = (tmplId: string) => {
    setSelectedTemplateId(tmplId);
    if (!tmplId) return;
    const tmpl = templates.find(t => t.id === tmplId);
    if (!tmpl) return;

    if (Array.isArray(tmpl.items) && tmpl.items.length > 0) {
      const newItems: InvoiceLineItem[] = tmpl.items.map((it: any) => ({
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        productId: it.productId || '',
        name: it.name || '',
        description: it.description || '',
        quantity: it.quantity || 1,
        rate: it.rate || 0,
        discountType: it.discountType || 'fixed',
        discountValue: it.discountValue || 0,
        discountAmount: it.discountAmount || 0,
        amount: it.amount || ((it.quantity || 1) * (it.rate || 0)),
        isCustom: !it.productId,
      }));
      setItems(newItems);
    }
    if (tmpl.notes) setNotes(tmpl.notes);
    if (tmpl.currency) setCurrency(tmpl.currency);
  };

  // Line items
  const [items, setItems] = useState<InvoiceLineItem[]>([
    {
      id: 'item_' + Math.random().toString(36).substring(2, 9),
      name: '',
      description: '',
      quantity: 1,
      rate: 0,
      discountType: 'fixed',
      discountValue: 0,
      discountAmount: 0,
      amount: 0,
      isCustom: true,
    },
  ]);

  const [adjustment, setAdjustment] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [status, setStatus] = useState<'Draft' | 'Sent'>('Draft');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const filteredProducts = products.filter(p =>
    !productSearch ||
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.description?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.productId?.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Populate from existing invoice if editing a draft
  useEffect(() => {
    if (existingInvoice) {
      setApplicantId(existingInvoice.applicantId || preselectedApplicationId || '');
      setInvoiceDate(existingInvoice.invoiceDate ? existingInvoice.invoiceDate.slice(0, 10) : new Date().toISOString().slice(0, 10));
      setDueDate(existingInvoice.dueDate ? existingInvoice.dueDate.slice(0, 10) : new Date().toISOString().slice(0, 10));
      setCurrency(existingInvoice.currency || 'USD');
      setNotes(existingInvoice.notes || '');
      setInternalNotes(existingInvoice.internalNotes || '');
      setAdjustment(existingInvoice.adjustment || 0);
      setAdjustmentReason(existingInvoice.adjustmentReason || '');
      setStatus(existingInvoice.status === 'Sent' ? 'Sent' : 'Draft');
      if (existingInvoice.items && existingInvoice.items.length > 0) {
        setItems(existingInvoice.items);
      }
    } else {
      if (preselectedApplicationId) {
        setApplicantId(preselectedApplicationId);
      }
    }
  }, [existingInvoice, preselectedApplicationId]);

  if (!isOpen) return null;

  // Calculate line item total
  const calculateItemAmount = (item: Partial<InvoiceLineItem>): { discountAmount: number; amount: number } => {
    const qty = Math.max(0, Number(item.quantity || 0));
    const rate = Math.max(0, Number(item.rate || 0));
    const rawTotal = Math.round(qty * rate * 100) / 100;

    let discountAmount = 0;
    const discVal = Math.max(0, Number(item.discountValue || 0));
    if (item.discountType === 'percentage') {
      discountAmount = Math.round(((rawTotal * Math.min(100, discVal)) / 100) * 100) / 100;
    } else {
      discountAmount = Math.min(rawTotal, Math.round(discVal * 100) / 100);
    }

    const amount = Math.max(0, Math.round((rawTotal - discountAmount) * 100) / 100);
    return { discountAmount, amount };
  };

  const handleUpdateItem = (id: string, updates: Partial<InvoiceLineItem>) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, ...updates };
        const { discountAmount, amount } = calculateItemAmount(updated);
        return { ...updated, discountAmount, amount };
      })
    );
  };

  const handleSelectProduct = (id: string, productId: string) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    handleUpdateItem(id, {
      productId: prod.id,
      name: prod.name,
      description: prod.description || '',
      rate: prod.sellingPrice || 0,
      isCustom: false,
    });
  };

  const handleAddItem = () => {
    setItems(prev => [
      ...prev,
      {
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        discountType: 'fixed',
        discountValue: 0,
        discountAmount: 0,
        amount: 0,
        isCustom: true,
      },
    ]);
  };

  const handleRemoveItem = (id: string) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(i => i.id !== id));
  };

  // Calculations (rounded to 2 decimal places to prevent float precision drift)
  const subtotal = Math.round(items.reduce((sum, item) => sum + (item.quantity * item.rate), 0) * 100) / 100;
  const totalDiscount = Math.round(items.reduce((sum, item) => sum + (item.discountAmount || 0), 0) * 100) / 100;
  const totalAfterDiscount = Math.max(0, Math.round((subtotal - totalDiscount) * 100) / 100);
  const grandTotal = Math.max(0, Math.round((totalAfterDiscount + Number(adjustment || 0)) * 100) / 100);

  const handleSubmit = async (submitStatus: 'Draft' | 'Sent') => {
    setError('');
    if (!applicantId) {
      setError('Please select an applicant.');
      return;
    }
    if (items.length === 0 || items.some(i => !i.name.trim())) {
      setError('Please provide a name/description for all invoice line items.');
      return;
    }

    const app = effectiveApplications.find(a => a.applicationId === applicantId);
    if (!app) {
      setError('Selected application not found.');
      return;
    }
    // Stored application docs carry richer program context than the Application
    // type declares, so read through `any` when stamping it onto the invoice.
    const src = app as any;

    setIsSubmitting(true);
    try {
      const payload: Partial<InvoiceRecord> = {
        invoiceId: existingInvoice?.invoiceId,
        applicantId: app.applicationId,
        applicantUserId: app.userId || '',
        applicantName: getSafeApplicantName(app),
        applicantEmail: app.userEmail || '',
        applicantAddress: [
          app.personalDetails?.address?.street,
          app.personalDetails?.address?.city,
          app.personalDetails?.address?.state,
          app.personalDetails?.address?.country,
        ].filter(Boolean).join(', '),
        programId: src.programId || src.assignment?.programId || '',
        programName: src.programName || src.assignment?.programName || src.programType || '',
        term: src.term || src.programTerm || src.assignment?.programTerm || '',
        referrerId: src.referredBy || '',
        referrerName: src.referredBy || src.referrerName || '',
        sponsorId: src.sponsorName || src.assignedSponsor || '',
        sponsorName: src.sponsorName || src.assignedSponsor || '',
        invoiceDate,
        dueDate,
        currency,
        items,
        subtotal,
        totalDiscount,
        adjustment: Number(adjustment || 0),
        adjustmentReason,
        total: grandTotal,
        amountPaid: existingInvoice?.amountPaid || 0,
        amountDue: Math.max(0, grandTotal - (existingInvoice?.amountPaid || 0)),
        status: submitStatus,
        notes,
        internalNotes,
        attachments: existingInvoice?.attachments || [],
        updatedBy: auth.currentUser?.email || 'admin',
      };

      await onSave(payload);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save invoice.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden text-slate-800 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/50">
          <div>
            <h3 className="text-base font-extrabold text-slate-900 font-display">
              {existingInvoice ? `Edit Draft Invoice #${existingInvoice.invoiceNumber}` : 'Create New Invoice'}
            </h3>
            <p className="text-caption text-slate-500">
              {existingInvoice ? 'Update line items and terms before sending.' : 'Select a template from Finance Management or build manually.'}
            </p>
          </div>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Prompt Screen */}
        {creationMode === 'prompt' && !existingInvoice ? (
          <div className="p-8 space-y-6 overflow-y-auto flex-1 text-xs">
            <div className="text-center max-w-md mx-auto space-y-2">
              <div className="h-12 w-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto">
                <FileText className="h-6 w-6" />
              </div>
              <h3 className="text-base font-black text-slate-900">How would you like to create this invoice?</h3>
              <p className="text-slate-500 text-xs">
                Select from pre-configured workspace templates in Finance Management or start with a custom blank invoice.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto pt-2">
              {/* Option 1: Select Template */}
              <div className="bg-slate-50 hover:bg-slate-100/80 border-2 border-indigo-200 hover:border-indigo-400 p-5 rounded-2xl transition-all flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-indigo-700 font-black text-sm">
                    <Sparkles className="h-4 w-4" />
                    <span>Select from Template(s)</span>
                  </div>
                  <p className="text-slate-500 text-[11px] leading-relaxed">
                    Choose from standard program fee packages, visa fees, and fee schedules saved in Finance Management.
                  </p>

                  <div className="pt-2 space-y-3">
                    {/* Applicant Selector */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-600 block">Select Applicant *</label>
                      <select
                        value={applicantId}
                        onChange={(e) => setApplicantId(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs font-semibold text-slate-800 outline-none focus:border-brand"
                      >
                        <option value="">-- Choose Applicant --</option>
                        {effectiveApplications.map((app) => {
                          const aId = app.applicationId || (app as any).id;
                          return (
                            <option key={aId} value={aId}>
                              {applicantOption(app)}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {applicantId && (
                      <div className="space-y-2 p-3 bg-white rounded-xl border border-slate-200">
                        <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                          Assignment Sequence (Program → Variable → Sponsor)
                        </div>

                        {/* Program Picker */}
                        {!selectedProgramId ? (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px]">
                            <strong>Program Missing:</strong> Select a program for this applicant to view available pricing variables.
                            <select
                              value={selectedProgramId}
                              onChange={(e) => {
                                setSelectedProgramId(e.target.value);
                                setSelectedVariableId('');
                                setSelectedSponsorId('');
                              }}
                              className="w-full mt-1.5 bg-white border border-amber-300 rounded p-1 text-xs font-semibold text-slate-800"
                            >
                              <option value="">-- Assign Program --</option>
                              {programsList.map((p) => (
                                <option key={p.id || p.programId} value={p.id || p.programId}>
                                  {p.name} ({p.code || 'No code'})
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : !selectedVariableId ? (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px]">
                            <strong>Pricing Variable Missing:</strong> Select a pricing variable for <strong>{activeProgramObj?.name || 'Program'}</strong>.
                            <select
                              value={selectedVariableId}
                              onChange={(e) => {
                                setSelectedVariableId(e.target.value);
                                setSelectedSponsorId('');
                              }}
                              className="w-full mt-1.5 bg-white border border-amber-300 rounded p-1 text-xs font-semibold text-slate-800"
                            >
                              <option value="">-- Assign Pricing Variable --</option>
                              {availableVariables.map((v: any) => (
                                <option key={v.id} value={v.id}>
                                  {v.name} ({v.code || 'No code'})
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : !selectedSponsorId ? (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px]">
                            <strong>Sponsor/Partner Missing:</strong> Assign an eligible sponsor with an active ACD relationship.
                            <select
                              value={selectedSponsorId}
                              onChange={(e) => setSelectedSponsorId(e.target.value)}
                              className="w-full mt-1.5 bg-white border border-amber-300 rounded p-1 text-xs font-semibold text-slate-800"
                            >
                              <option value="">-- Assign Partner / Sponsor --</option>
                              {availableAcdSponsors.map((s: any) => {
                                const sId = s.sponsorId || s.id;
                                const sName = s.name || s.companyName || s.displayName || sId;
                                return (
                                  <option key={sId} value={sId}>
                                    {sName} {s.code ? `(${s.code})` : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        ) : (
                          <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] text-emerald-800 font-medium">
                            <Check className="w-3.5 h-3.5 inline text-emerald-600 mr-1" />
                            <strong>Assigned Sequence Complete:</strong> {activeProgramObj?.name} &bull; {availableVariables.find((v: any) => v.id === selectedVariableId)?.name} &bull; {sponsorsList.find(s => (s.sponsorId || s.id) === selectedSponsorId)?.name || 'Sponsor'}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Matching Templates List */}
                    <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block">
                      Matching Templates ({matchingTemplates.length})
                    </label>

                    {!selectedProgramId || !selectedVariableId || !selectedSponsorId ? (
                      <p className="text-[11px] text-slate-500 italic bg-white p-3 rounded-xl border border-slate-200 text-center">
                        Complete the Program → Variable → Sponsor assignment above to unlock available templates.
                      </p>
                    ) : matchingTemplates.length > 0 ? (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {matchingTemplates.map(tmpl => (
                          <button
                            key={tmpl.id}
                            type="button"
                            onClick={() => {
                              handleApplyTemplate(tmpl.id);
                              setCreationMode('builder');
                            }}
                            className="w-full text-left p-2.5 bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/50 rounded-xl transition-all cursor-pointer group"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-slate-800 group-hover:text-indigo-900 text-xs">
                                {tmpl.code ? `[${tmpl.code}] ` : ''}{tmpl.name}
                              </span>
                              <span className="font-mono font-bold text-indigo-600 text-[11px]">${(tmpl.total || 0).toFixed(2)}</span>
                            </div>
                            <p className="text-[11px] text-slate-500 truncate mt-0.5">{tmpl.description || `${tmpl.items?.length || 0} pre-loaded item(s)`}</p>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-amber-700 italic bg-amber-50 p-3 rounded-xl border border-amber-200 text-center">
                        No matching template configured for this exact Program, Variable, and Sponsor combination. Configure a matching template in Workspace Settings or click "Custom Blank Invoice" below.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Option 2: Create Manually */}
              <div className="bg-slate-50 hover:bg-slate-100/80 border-2 border-slate-200 hover:border-slate-400 p-5 rounded-2xl transition-all flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-800 font-black text-sm">
                    <Plus className="h-4 w-4 text-emerald-600" />
                    <span>Create Manually</span>
                  </div>
                  <p className="text-slate-500 text-[11px] leading-relaxed">
                    Build a custom invoice from scratch. Select catalogue items or enter custom line item names, rates, and discounts manually.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setCreationMode('builder')}
                  className="w-full py-3 bg-brand hover:bg-brand-active text-white font-bold rounded-xl text-xs transition-all cursor-pointer shadow-md text-center"
                >
                  Start Blank Invoice &rarr;
                </button>
              </div>
            </div>
          </div>
        ) : (

        /* Body (Builder Form) */
        <div className="p-6 space-y-6 overflow-y-auto flex-1 text-xs">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" />
              <span>{error}</span>
            </div>
          )}

          {/* Applicant and Dates Bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200/80">
            <div className="md:col-span-2 space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Target Applicant / Bill To *</label>
              <SearchableSelect
                value={applicantId}
                onChange={(val) => setApplicantId(val)}
                disabled={!!preselectedApplicationId && !!existingInvoice}
                options={effectiveApplications.map(app => ({
                  value: app.applicationId || (app as any).id,
                  label: getSafeApplicantName(app),
                  hint: `${app.applicationId || ''} · ${app.userEmail || ''}`.trim(),
                  tag: app.programType || undefined
                }))}
                placeholder="Search candidate by name, email or ID..."
                allowClear={true}
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Invoice Date *</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={e => setInvoiceDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold text-slate-800 outline-none focus:border-brand"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Due Date *</label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg p-2 font-bold text-slate-800 outline-none focus:border-brand"
              />
            </div>
          </div>

          {/* Line Items Table */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="font-extrabold text-slate-900 text-xs uppercase tracking-wider">Invoice Line Items</h4>
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[11px] transition-all cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" /> Add Custom Line Item
              </button>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">
                    <th className="p-3 w-1/4">Product / Item</th>
                    <th className="p-3 w-1/3">Description</th>
                    <th className="p-3 w-16 text-center">Qty</th>
                    <th className="p-3 w-24 text-right">Rate</th>
                    <th className="p-3 w-32 text-right">Discount</th>
                    <th className="p-3 w-28 text-right">Amount</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-slate-50/50">
                      <td className="p-3 align-top space-y-1.5">
                        {/* Catalogue Selector */}
                        <div className="space-y-1">
                          {products.length > 5 && (
                            <input
                              type="text"
                              placeholder="Search catalog..."
                              value={productSearch}
                              onChange={e => setProductSearch(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-lg p-1 text-[10px] text-slate-700 outline-none"
                            />
                          )}
                          <SearchableSelect
                            value={item.productId || ''}
                            onChange={v => handleSelectProduct(item.id, v)}
                            options={filteredProducts.map(p => ({
                              value: p.id,
                              label: `${tidy(p.name, 40)} — $${Number(p.sellingPrice || p.price || 0).toLocaleString()}`,
                            }))}
                            placeholder="Search catalogue…"
                            clearLabel="-- From Catalogue --"
                            emptyLabel="No products found"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Item Name *"
                          value={item.name}
                          onChange={e => handleUpdateItem(item.id, { name: e.target.value, isCustom: !item.productId })}
                          className="w-full bg-white border border-slate-200 rounded-lg p-1.5 font-bold text-slate-800 outline-none focus:border-brand"
                        />
                      </td>

                      <td className="p-3 align-top">
                        <textarea
                          rows={3}
                          placeholder="Description / details..."
                          value={item.description}
                          onChange={e => handleUpdateItem(item.id, { description: e.target.value })}
                          className="w-full bg-white border border-slate-200 rounded-lg p-1.5 text-slate-600 outline-none focus:border-brand resize-y"
                        />
                      </td>

                      <td className="p-3 align-top text-center">
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={e => handleUpdateItem(item.id, { quantity: Number(e.target.value) })}
                          className="w-14 bg-white border border-slate-200 rounded-lg p-1.5 text-center font-bold text-slate-800 outline-none focus:border-brand"
                        />
                      </td>

                      <td className="p-3 align-top text-right">
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.rate}
                          onChange={e => handleUpdateItem(item.id, { rate: Number(e.target.value) })}
                          className="w-20 bg-white border border-slate-200 rounded-lg p-1.5 text-right font-bold text-slate-800 outline-none focus:border-brand font-mono"
                        />
                      </td>

                      <td className="p-3 align-top">
                        <div className="flex items-center gap-1 justify-end">
                          <select
                            value={item.discountType || 'fixed'}
                            onChange={e => handleUpdateItem(item.id, { discountType: e.target.value as any })}
                            className="bg-slate-50 border border-slate-200 rounded-lg p-1 text-[10px] font-bold"
                          >
                            <option value="fixed">$</option>
                            <option value="percentage">%</option>
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            value={item.discountValue || 0}
                            onChange={e => handleUpdateItem(item.id, { discountValue: Number(e.target.value) })}
                            className="w-16 bg-white border border-slate-200 rounded-lg p-1 text-right font-bold text-slate-800 outline-none focus:border-brand font-mono"
                          />
                        </div>
                        {item.discountAmount > 0 && (
                          <div className="text-[11px] text-emerald-600 text-right font-bold mt-1">
                            -${item.discountAmount.toFixed(2)}
                          </div>
                        )}
                      </td>

                      <td className="p-3 align-top text-right font-bold font-mono text-slate-900 pt-3">
                        ${Number(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      <td className="p-3 align-top text-center pt-2">
                        <button aria-label="Remove item" title="Remove item"
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={items.length <= 1}
                          className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 cursor-pointer transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Calculations Summary & Manual Adjustment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-200/80">
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Manual Dollar Adjustment (+ / -)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={adjustment}
                    onChange={e => setAdjustment(Number(e.target.value))}
                    className="w-32 bg-white border border-slate-200 rounded-lg p-2 font-bold font-mono text-slate-800 outline-none focus:border-brand"
                  />
                  <input
                    type="text"
                    placeholder="Reason for adjustment (e.g. Early Bird Waiver)"
                    value={adjustmentReason}
                    onChange={e => setAdjustmentReason(e.target.value)}
                    className="flex-1 bg-white border border-slate-200 rounded-lg p-2 font-medium text-slate-700 outline-none focus:border-brand"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Customer Terms & Notes</label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 font-medium text-slate-700 outline-none focus:border-brand resize-y"
                />
              </div>
            </div>

            {/* Subtotal & Totals Box */}
            <div className="space-y-2 bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-center">
              <div className="flex justify-between items-center text-slate-600">
                <span>Subtotal:</span>
                <span className="font-mono font-bold">${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex justify-between items-center text-emerald-600">
                  <span>Line Item Discounts:</span>
                  <span className="font-mono font-bold">-${totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              {adjustment !== 0 && (
                <div className="flex justify-between items-center text-indigo-600">
                  <span>Manual Adjustment:</span>
                  <span className="font-mono font-bold">{adjustment > 0 ? '+' : ''}${adjustment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <hr className="border-slate-100 my-1" />
              <div className="flex justify-between items-center text-slate-900 font-extrabold text-sm">
                <span>Total Amount Due:</span>
                <span className="font-mono text-base text-blue-600">${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}</span>
              </div>
            </div>
          </div>

          {/* Internal Notes */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Internal Confidential Notes (Admins Only - Hidden from Applicant)</label>
            <textarea
              rows={3}
              placeholder="e.g. Approved by Regional Coordinator; manual price discount granted."
              value={internalNotes}
              onChange={e => setInternalNotes(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-medium text-slate-700 outline-none focus:bg-white focus:border-brand resize-y"
            />
          </div>
        </div>
        )}

        {/* Footer Actions */}
        {creationMode === 'builder' || existingInvoice ? (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50/50">
            <button
              type="button"
              onClick={() => {
                if (!existingInvoice) {
                  setCreationMode('prompt');
                } else {
                  onClose();
                }
              }}
              className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-xl font-bold transition-all cursor-pointer"
            >
              {!existingInvoice ? '← Change Mode' : 'Cancel'}
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleSubmit('Draft')}
                className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50"
              >
                Save as Draft
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  if (!applicantId) {
                    setError('Please select an applicant.');
                    return;
                  }
                  setShowEmailConfirm(true);
                }}
                className="px-5 py-2 btn-primary text-white font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 shadow-md shadow-blue-500/20"
              >
                {isSubmitting ? 'Saving...' : 'Finalize & Issue Invoice'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-xl font-bold transition-all cursor-pointer"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {showEmailConfirm && (
        <Modal onClose={() => setShowEmailConfirm(false)} ariaLabel="Confirm Finalize Invoice">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md w-full space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-2xl bg-blue-50 text-blue-600">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-slate-900">Finalize & Issue Invoice?</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Issue invoice for total <strong>${grandTotal.toFixed(2)} {currency}</strong> to target applicant?
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 text-xs text-slate-600 space-y-1">
              <div>• Status: <strong>Sent / Issued</strong></div>
              <div>• Payment Link: <strong>Auto-generated secure payment URL</strong></div>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setShowEmailConfirm(false)}
                className="px-4 py-2 border border-slate-200 text-xs font-bold text-slate-600 rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setShowEmailConfirm(false);
                  handleSubmit('Sent');
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all cursor-pointer shadow-sm"
              >
                {isSubmitting ? 'Finalizing...' : 'Finalize & Issue'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
